/**
 * The Ankiety tab's one toolbar line: what is assigned and the act that assigns, on the left; how
 * many surveys the window holds and the window itself, on the right.
 *
 * ── One light line, not two cards ────────────────────────────────────────────────────────────
 * Owner, 4 November 2026 (canvas page „Finał", row F6): the „Zakres" card and the „Przypisane
 * ankiety" card were two bars of chrome around one button and two date fields. They are one line
 * now, on the tab's ground. The window is a segmented choice of three ready spans — eight weeks is
 * the tab's default — and a fourth segment that says the window in words („9 wrz – 4 lis") and
 * opens the two date fields in a popover, for a span of the trainer's own.
 *
 * ── Every part holds its box through the wait ────────────────────────────────────────────────
 * The assigned names wait as a bar the width of a name, the count as a blank of its own reserve,
 * and neither the segmented control nor „Przypisz ankietę" depends on a read at all.
 */

import { Popover } from '@base-ui/react/popover';
import { Link } from '@tanstack/react-router';
import { motion, type HTMLMotionProps } from 'motion/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { DateField } from '~/shared/ui/DateField';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import menuStyles from '~/shared/ui/MenuSurface.module.css';
import seg from '~/shared/ui/segmented.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { EnrolledSurveysState } from '../lib/useEnrolledSurveys';
import {
  presetSurveysRange,
  SURVEY_RANGE_PRESETS,
  surveysRangeLabel,
  surveysRangePreset,
} from '../model/surveyRange';
import type { TrendsRange, TrendsRangeView } from '../model/trendsRange';
import styles from './SurveysTab.module.css';

interface SurveysToolbarProps {
  readonly enrolled: EnrolledSurveysState;
  readonly gate: SkeletonGate;
  /** `null` until the card's header read has named the client. */
  readonly onAssignSurvey: ((opener: HTMLElement) => void) | null;
  readonly range: TrendsRange;
  /** The two fields' bounds and the refusal, from the tab's own `model/` decision. */
  readonly bounds: TrendsRangeView;
  readonly onRangeChange: (range: TrendsRange) => void;
  /** „9 ankiet w zakresie", or a blank while the read has not answered. */
  readonly count: string;
  readonly entrance: HTMLMotionProps<'div'>;
}

export function SurveysToolbar({
  enrolled,
  gate,
  onAssignSurvey,
  range,
  bounds,
  onRangeChange,
  count,
  entrance,
}: SurveysToolbarProps) {
  const { t, i18n } = useTranslation();
  const rangeLabelId = useId();
  const refusalId = useId();
  const today = new Date();
  const preset = surveysRangePreset(range, today);
  const surveys = enrolled.enrolled;

  return (
      <motion.div className={styles.toolbar} {...entrance}>
        <div className={styles.toolbarSide}>
          <span className={styles.toolbarLabel}>{t('web:clientDetail.surveys.assignedLabel')}</span>
          <span className={styles.toolbarAssigned} aria-busy={gate.waiting}>
            {gate.waiting ? (
              <>
                <span className={hidden.srOnly}>{t('web:clientDetail.surveys.enrolledLoading')}</span>
                {gate.skeleton ? (
                  <i className={`${String(skel.bar)} ${String(styles.toolbarSkel)}`} aria-hidden="true" />
                ) : null}
              </>
            ) : enrolled.failed || surveys === null ? (
              <span className={`${String(styles.toolbarMeta)} ${gate.reveal}`}>
                {t('web:clientDetail.surveys.enrolledFailed')}{' '}
                <button type="button" className={inlineButton.inlineTextButton} onClick={enrolled.retry}>
                  {t('web:clientDetail.states.retry')}
                </button>
              </span>
            ) : surveys.length === 0 ? (
              <span className={`${String(styles.toolbarMeta)} ${gate.reveal}`}>
                {t('web:clientDetail.surveys.enrolledNone')}
              </span>
            ) : (
              <span className={`${String(styles.toolbarNames)} ${gate.reveal}`}>
                {surveys.map((survey, index) => (
                  <span key={survey.id}>
                    {index === 0 ? null : ', '}
                    <Link
                      to="/raporty/$templateId"
                      params={{ templateId: String(survey.id) }}
                      className={styles.toolbarSurvey}
                    >
                      {survey.title}
                    </Link>
                  </span>
                ))}
                {/* One form's rhythm is said beside it; several forms have several, and their names
                  * already lead to the editor that states each. */}
                {surveys.length === 1 && surveys[0] !== undefined ? (
                  <span className={`${String(styles.toolbarMeta)} ${String(styles.toolbarCadence)}`}>
                    {[
                      surveys[0].cadence,
                      surveys[0].paused ? t('web:clientDetail.surveys.enrolledPaused') : null,
                    ]
                      .filter((part) => part !== null)
                      .join(' · ')}
                  </span>
                ) : null}
              </span>
            )}
          </span>
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(styles.assignBar)}`}
            aria-disabled={onAssignSurvey === null ? true : undefined}
            onClick={(event) => {
              onAssignSurvey?.(event.currentTarget);
            }}
          >
            {t('web:clientDetail.surveys.assign')}
          </button>
        </div>

        <div className={styles.toolbarSide}>
          <span className={styles.toolbarCount}>{count}</span>
          <div className={seg.seg} role="group" aria-label={t('web:clientDetail.surveys.rangeLabel')}>
            {SURVEY_RANGE_PRESETS.map((entry) => (
              <button
                key={entry}
                type="button"
                className={`${String(seg.segOption)} ${String(styles.rangeOption)} ${
                  preset === entry ? String(seg.segOptionOn) : ''
                }`}
                aria-pressed={preset === entry}
                onClick={() => {
                  onRangeChange(presetSurveysRange(entry, today));
                }}
              >
                {t(`web:clientDetail.surveys.rangePreset.${entry}`)}
              </button>
            ))}
            <Popover.Root>
              <Popover.Trigger
                className={`${String(seg.segOption)} ${String(styles.rangeOption)} ${
                  preset === null ? String(seg.segOptionOn) : ''
                }`}
                aria-label={t('web:clientDetail.surveys.rangeCustom', {
                  range: surveysRangeLabel(range, i18n.language),
                })}
              >
                {surveysRangeLabel(range, i18n.language)}
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Positioner sideOffset={6} align="end">
                  <Popover.Popup className={`${String(menuStyles.popup)} ${String(styles.rangePopup)}`}>
                    <Popover.Title className={styles.rangePopupTitle} id={rangeLabelId}>
                      {t('web:clientDetail.surveys.rangeLabel')}
                    </Popover.Title>
                    <div className={styles.rangeFields}>
                      <span className={styles.rangeField}>
                        <DateField
                          label={t('web:clientDetail.trends.from')}
                          value={range.from}
                          min={bounds.fromMin}
                          max={bounds.fromMax}
                          invalid={!bounds.valid}
                          describedBy={bounds.valid ? undefined : refusalId}
                          onChange={(from) => {
                            if (from !== null) onRangeChange({ ...range, from });
                          }}
                        />
                      </span>
                      <span aria-hidden="true">→</span>
                      <span className={styles.rangeField}>
                        <DateField
                          label={t('web:clientDetail.trends.to')}
                          value={range.to}
                          min={bounds.toMin}
                          max={bounds.toMax}
                          invalid={!bounds.valid}
                          describedBy={bounds.valid ? undefined : refusalId}
                          onChange={(to) => {
                            if (to !== null) onRangeChange({ ...range, to });
                          }}
                        />
                      </span>
                    </div>
                    {bounds.refusal === null ? null : (
                      <p className={styles.rangeRefusal} id={refusalId} role="alert">
                        {bounds.refusal}
                      </p>
                    )}
                  </Popover.Popup>
                </Popover.Positioner>
              </Popover.Portal>
            </Popover.Root>
          </div>
        </div>
      </motion.div>
  );
}
